/* eslint-disable import/no-unresolved */
import { gsap } from 'https://cdn.skypack.dev/gsap'
import { Draggable } from 'https://cdn.skypack.dev/gsap/Draggable'
import { InertiaPlugin } from 'https://cdn.skypack.dev/gsap/InertiaPlugin'
import { MotionPathPlugin } from 'https://cdn.skypack.dev/gsap/MotionPathPlugin'
/* eslint-enable import/no-unresolved */

gsap.registerPlugin(Draggable, InertiaPlugin, MotionPathPlugin)

class AdventCalendar extends window.BAO.CustomElement() {
  constructor () {
    super()

    this.canvas = this.els.canvas.element
    this.context = this.canvas.getContext('2d')
    this.updateCanvasSize()

    this.carouselState = { rotation: 0 }

    this.landmarks = gsap.utils.toArray(this.els.landmark.selector)
    this.trees = gsap.utils.toArray(this.els.tree.selector)
    this.dolls = gsap.utils.toArray(this.els.doll.selector)
    this.landmarksAmount = this.landmarks.length
    this.step = 360 / this.landmarksAmount
    this.baseOffsets = this.landmarks.map(
      (_, i) => i / (this.landmarksAmount / 2),
    )
    this.landmarkStep = this.step * 2
    this.moveDelay = 0.1
    this.moveDuration = 1.5
    this.currentPath =
      window.innerWidth < 768
        ? this.els['path-mobile'].selector
        : this.els['path-desktop'].selector

    this.particles = []
    this.initParticles()
    this.animate()

    this.setLandmarkPositions(0)
    this.initialize()
    this.moveToCurrentDay()
  }

  updateCanvasSize () {
    this.containerRect = this.els.container.element.getBoundingClientRect()
    this.canvas.width = this.containerRect.width
    this.canvas.height = this.containerRect.height
  }

  setupListeners () {
    super.setupListeners()

    window.addEventListener('resize', () => {
      this.currentPath =
        window.innerWidth < 768
          ? this.els['path-mobile'].selector
          : this.els['path-desktop'].selector
      this.setLandmarkPositions(this.carouselState.rotation)
      this.updateCanvasSize()
      this.particles = []
      this.initParticles()
    })

    const bindLandmark = el =>
      this.listeners.add(el, 'click', this.setTodayLandmark.bind(this, el))
    this.els.landmark?.elements.forEach(bindLandmark)
    this.els['today-trigger']?.elements.forEach(bindLandmark)
    this.externalEls['today-trigger']?.elements.forEach(bindLandmark)

    this.listeners.add(
      this.els.next.element,
      'click',
      this.moveCarousel.bind(this, -this.landmarkStep),
    )
    this.listeners.add(
      this.els.previous.element,
      'click',
      this.moveCarousel.bind(this, this.landmarkStep),
    )
  }

  setLandmarkPositions (rotation) {
    const trees = this.trees
    const dolls = this.dolls
    const step2 = this.step * 2
    const landmarks = this.landmarks
    const currentPath = this.currentPath
    const baseOffsets = this.baseOffsets
    const landmarkTitleEl = this.els['landmark-title'].element

    let activeTitle = null

    gsap.utils.toArray(landmarks).forEach((landmark, i) => {
      const startingPoint = step2 * i
      const progress = (baseOffsets[i] + rotation / 360) % 1
      const currentRotation = (rotation + startingPoint) * -1

      const isActive = currentRotation <= 29 && currentRotation >= -29
      landmark.toggleAttribute('data-active', isActive)
      if (isActive) activeTitle = landmark.dataset.landmarkTitle

      const hide =
        (i >= 2 && currentRotation <= -95) ||
        (i <= 6 && currentRotation >= 265) ||
        (currentRotation >= 95 && currentRotation <= 265)

      landmark.setAttribute('aria-hidden', hide ? 'true' : 'false')

      if (!hide) {
        gsap.set(landmark, {
          motionPath: {
            path: currentPath,
            align: currentPath,
            alignOrigin: [0.5, 0.93],
            autoRotate: true,
            start: -95,
            end: progress,
          },
        })
      }
    })

    if (activeTitle) {
      landmarkTitleEl.innerText = activeTitle
    }

    gsap.utils.toArray(trees).forEach((tree, i) => {
      const startingPoint = step2 * i
      const currentRotation = (rotation + startingPoint) * -1
      const progress = (baseOffsets[i] + rotation / 360 + 26 / 360) % 1

      const isFirst = i === 0
      const isFifth = i === 4
      const hideFirst = isFirst && currentRotation >= 630
      const hideFifth = isFifth && currentRotation <= -90
      tree.setAttribute(
        'aria-hidden',
        hideFirst || hideFifth ? 'true' : 'false',
      )

      gsap.set(tree, {
        motionPath: {
          path: currentPath,
          align: currentPath,
          alignOrigin: [0.5, 1],
          autoRotate: true,
          start: progress,
          end: progress,
        },
      })
    })

    gsap.utils.toArray(dolls).forEach((doll, i) => {
      const startingPoint = step2 * i
      const currentRotation = (rotation + startingPoint) * -1
      const progress = (baseOffsets[i] + rotation / 360 + 32 / 360) % 1

      const isFirst = i === 0
      const isFifth = i === 4
      const hideFirst = isFirst && currentRotation >= 630
      const hideFifth = isFifth && currentRotation <= -90
      doll.setAttribute(
        'aria-hidden',
        hideFirst || hideFifth ? 'true' : 'false',
      )

      gsap.set(doll, {
        motionPath: {
          path: currentPath,
          align: currentPath,
          alignOrigin: [0.5, 1],
          autoRotate: true,
          start: progress,
          end: progress,
        },
      })
    })

    this.setAttribute('data-initialised', 'true')
  }

  //   gsap.utils.toArray(dolls).forEach((doll, i) => {
  //     const progress = (baseOffsets[i] + rotation / 360 + 34 / 360) % 1

  //     const isFirst = i === 0
  //     const isLast = i === dolls.length - 1
  //     const hideFirst = isFirst && rotation <= -(720 - this.step * 2)
  //     const hideLast = isLast && rotation >= 90
  //     doll.setAttribute('aria-hidden', hideFirst || hideLast ? 'true' : 'false')

  //     gsap.set(doll, {
  //       motionPath: {
  //         path: currentPath,
  //         align: currentPath,
  //         alignOrigin: [0.5, 1],
  //         autoRotate: true,
  //         start: progress,
  //         end: progress,
  //       },
  //     })
  //   })

  //   this.setAttribute('data-initialised', 'true')
  // }

  moveCarousel (delta) {
    const minRotation = 0
    const maxRotation = -(720 - this.step * 2)

    let newRotation = this.carouselState.rotation + delta
    if (newRotation > minRotation) newRotation = minRotation
    if (newRotation < maxRotation) newRotation = maxRotation

    // Only update buttons if state actually changes
    const atFirst = newRotation === minRotation
    const atLast = newRotation === maxRotation
    this.els.next.element.setAttribute('aria-hidden', atLast ? 'true' : 'false')
    this.els.previous.element.setAttribute(
      'aria-hidden',
      atFirst ? 'true' : 'false',
    )

    if (this._isAnimating) return
    this._isAnimating = true
    this.setAttribute('data-disabled', '')

    gsap.to(this.carouselState, {
      rotation: newRotation,
      delay: this.moveDelay,
      duration: this.moveDuration,
      ease: 'circ.inOut',
      onUpdate: () => this.setLandmarkPositions(this.carouselState.rotation),
      onComplete: () => {
        gsap.set(this.els['container-inner'].selector, {
          rotation: this.carouselState.rotation,
        })
        this.removeAttribute('data-disabled')
        this._isAnimating = false
      },
    })
  }

  moveToCurrentDay () {
    setTimeout(() => {
      const targetIndex =
        parseInt(this.els['today-trigger'].elements[0].dataset.currentDay, 10) -
        1
      const currentIndex = this.landmarks.findIndex(landmark =>
        landmark.hasAttribute('data-active'),
      )
      const deltaIndex = targetIndex - currentIndex

      this.moveCarousel(-this.landmarkStep * deltaIndex)
    }, 2000)
  }

  onResize () {
    const prevWidth = this.canvas.width
    const prevHeight = this.canvas.height

    this.containerRect = this.els.container.element.getBoundingClientRect()

    if (
      prevWidth !== this.containerRect.width ||
      prevHeight !== this.containerRect.height
    ) {
      this.canvas.width = this.containerRect.width
      this.canvas.height = this.containerRect.height

      this.particles = []
      this.initParticles()
    }
  }

  setTodayLandmark (button) {
    const activeDrawer = document.querySelector('.drw-Drawer-active')
    if (activeDrawer) {
      activeDrawer.querySelector('[data-drawers-close]')?.click()
    }

    const landmarks = this.landmarks
    const targetIndex = parseInt(button.dataset.currentDay, 10) - 1
    const currentIndex = landmarks.findIndex(landmark =>
      landmark.hasAttribute('data-active'),
    )
    const deltaIndex = targetIndex - currentIndex
    const todaysLandmark = landmarks[targetIndex]

    if (deltaIndex !== 0) {
      this.moveCarousel(-this.landmarkStep * deltaIndex)
    }

    window.dispatchEvent(
      new CustomEvent('advent:drawer', {
        detail: {
          targetIndex,
          currentIndex,
          todaysLandmark,
          selectedDay: button.dataset.currentDay,
          title: `${todaysLandmark.dataset.index}. ${todaysLandmark.dataset.offerTitle}`,
        },
        bubbles: true,
      }),
    )

    const openDrawer = () =>
      todaysLandmark.parentElement
        .querySelector('[data-drawers-trigger="advent"]')
        ?.click()

    if (targetIndex === currentIndex) {
      requestAnimationFrame(openDrawer)
    } else {
      setTimeout(openDrawer, (this.moveDelay + this.moveDuration) * 1000 + 100)
    }
  }

  onDragUpdate (drag) {
    if (this.carouselState.rotation !== drag.rotation) {
      this.carouselState.rotation = drag.rotation
      this.setLandmarkPositions(drag.rotation)
    }

    if (drag.target.style.transform) {
      drag.target.style.transform = ''
    }

    const minRotation = 0
    const maxRotation = -(720 - this.step * 2)
    const halfwayStep = this.landmarkStep / 2

    const atFirstOrBeforeHalf = drag.rotation >= minRotation - halfwayStep
    const atLastOrAfterHalf = drag.rotation <= maxRotation + halfwayStep

    if (this._lastAtFirstOrBeforeHalf !== atFirstOrBeforeHalf) {
      this.els.previous.element.setAttribute(
        'aria-hidden',
        atFirstOrBeforeHalf ? 'true' : 'false',
      )
      this._lastAtFirstOrBeforeHalf = atFirstOrBeforeHalf
    }

    if (this._lastAtLastOrAfterHalf !== atLastOrAfterHalf) {
      this.els.next.element.setAttribute(
        'aria-hidden',
        atLastOrAfterHalf ? 'true' : 'false',
      )
      this._lastAtLastOrAfterHalf = atLastOrAfterHalf
    }
  }

  initialize () {
    const _this = this
    let lastUpdate = 0
    const throttleMs = 16

    if (this._draggable) {
      this._draggable[0].kill()
    }

    this._draggable = Draggable.create(this.els['container-inner'].selector, {
      type: 'rotation',
      inertia: true,
      zIndexBoost: false,
      dragClickables: false,
      dragResistance: 0.4,
      bounds: {
        minRotation: 0,
        maxRotation: -(720 - this.step * 2),
      },
      snap: endVal => gsap.utils.snap(this.step * 2, endVal),
      onDrag () {
        _this.setAttribute('data-disabled', '')
        const now = performance.now()
        if (now - lastUpdate > throttleMs) {
          _this.onDragUpdate(this)
          lastUpdate = now
        }
      },
      onThrowUpdate () {
        _this.setAttribute('data-disabled', '')
        const now = performance.now()
        if (now - lastUpdate > throttleMs) {
          _this.onDragUpdate(this)
          lastUpdate = now
        }
      },
      onThrowComplete () {
        _this.removeAttribute('data-disabled')
      },
    })
  }

  initParticles () {
    const isMobile = window.innerWidth < 768
    const particleAmount = isMobile ? 20 : 60
    const color = '#fff'
    const width = this.canvas.width
    const height = this.canvas.height
    const centerX = width / 2
    const centerY = height / 2
    const maxDist = Math.max(width, height)
    const particles = new Array(particleAmount)

    for (let i = 0; i < particleAmount; i++) {
      const angle = Math.random() * Math.PI * 2
      const radius = Math.sqrt(Math.random()) * (maxDist / 2)
      const x = centerX + Math.cos(angle) * radius
      const y = centerY + Math.sin(angle) * radius
      particles[i] = new Particle(
        x,
        y,
        this.randomIntFromRange(0.5, 3),
        color,
        Math.random() * Math.PI * 2,
        this.context,
      )
    }

    this.particles = particles
  }

  randomIntFromRange (min, max) {
    return Math.floor(Math.random() * (max - min + 1) + min)
  }

  animate () {
    const ctx = this.context
    const particles = this.particles

    const rect = this.canvas.getBoundingClientRect()
    const isVisible = rect.bottom > 0 && rect.top < window.innerHeight

    if (this.isConnected && isVisible) {
      requestAnimationFrame(this.animate.bind(this))
    }

    if (particles && particles.length && isVisible) {
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
      for (let i = 0, len = particles.length; i < len; i++) {
        particles[i].update()
      }
    }
  }
}

class Particle {
  constructor (x, y, _, color, radians, context) {
    this.x = x
    this.y = y
    this.color = color
    this.radians = radians
    this.context = context
    this.velocity = 0.3 + Math.random() * 0.15

    this.paths = [
      'M8.50391 5.01454C8.50391 8.36254 5.07218 8.69227 3.35631 8.43863C1.45217 8.43863 -0.320544 5.01454 0.0471411 3.11237C0.414826 1.21021 1.88557 0.449191 4.82705 0.068737C7.76854 -0.311718 8.50391 0.829536 8.50391 5.01454Z',
      'M7.35389 3.04448C7.94766 4.51045 5.37467 6.58723 4.01395 7.44238C-1.18206 7.44238 -0.80989 1.945 2.15895 0.479033C5.1278 -0.986936 6.61168 1.21202 7.35389 3.04448Z',
      'M5.79861 1.34309C7.48579 2.7756 5.09561 6.71498 3.68963 8.50562C2.86976 8.50562 1.08943 8.29074 0.527031 7.43124C-0.175963 6.35686 -0.175963 3.13372 0.527031 1.34309C1.23002 -0.447536 3.68963 -0.447536 5.79861 1.34309Z',
      'M2.18682 8.47023C-0.94244 8.10318 0.100645 1.86338 0.448341 0.395187C3.23004 -1.073 5.31593 1.86338 5.31593 4.06566V4.06596C5.31593 6.26818 5.31593 8.83726 2.18682 8.47023Z',
      'M9.57031 5.67045C9.57031 7.65507 6.72627 8.38748 5.30425 8.50562C3.88223 8.50562 0.883064 8.36386 0.262547 7.79682C-0.513101 7.08803 0.65037 4.25287 1.03819 2.83529C1.42602 1.4177 3.36514 0.000121117 6.46772 0.000121117C9.57031 0.000121117 9.57031 3.18968 9.57031 5.67045Z',
      'M6.29179 2.0759C6.88288 3.51591 4.32151 4.83592 2.96694 5.31592C-0.357915 5.31592 0.0120444 3.87591 0.0120444 2.0759C0.0120444 0.275892 1.48976 0.995895 2.96694 0.275892C4.44412 -0.444112 5.55293 0.275892 6.29179 2.0759Z',
      'M3.45247 0.849355C4.90395 2.30527 4.05725 5.33841 3.45247 6.673C-0.781605 9.58481 -0.175676 3.3972 0.42911 1.94129L0.429513 1.94032C1.03416 0.484731 1.63851 -0.970127 3.45247 0.849355Z',
      'M6.30962 2.76161C6.85664 3.81399 4.03036 5.61179 2.54884 6.37915C0.497666 6.37915 0.839235 4.40595 0.155457 3.74821C-0.528321 3.09048 1.18112 0.788409 2.54884 0.130675C3.91656 -0.52706 5.62584 1.44614 6.30962 2.76161Z',
      'M5.20303 1.00902C5.72078 2.08532 4.34011 4.14822 3.58506 5.04514C-1.26846 6.39052 -0.298456 2.35439 1.31951 1.00902C2.93748 -0.33636 4.55584 -0.33636 5.20303 1.00902Z',
      'M0.113376 4.3069C-0.404375 3.2306 0.976293 1.16769 1.73135 0.270777C6.58487 -1.0746 5.61486 2.96153 3.99689 4.3069C2.37892 5.65228 0.760564 5.65228 0.113376 4.3069Z',
    ]

    if (!Particle.pathIndex) Particle.pathIndex = 0
    this.pathData = this.paths[Particle.pathIndex % this.paths.length]
    Particle.pathIndex++

    this.path2D = new Path2D(this.pathData)

    this.pathWidth = 10
    this.pathHeight = 10

    this.resetIfNeeded = (canvasWidth, canvasHeight) => {
      if (this.y > canvasHeight + 20) {
        this.y = -Math.random() * 50 - 10
        this.x = Math.random() * canvasWidth
      }
    }
  }

  update () {
    this.y += this.velocity
    this.draw()
    this.resetIfNeeded(this.context.canvas.width, this.context.canvas.height)
  }

  draw () {
    const ctx = this.context
    const swayAmplitude = 8
    const swayFrequency = 0.015
    const sway = Math.sin(this.y * swayFrequency + this.radians) * swayAmplitude

    ctx.save()
    ctx.translate(this.x + sway, this.y)
    ctx.scale(1, 1)
    ctx.fillStyle = this.color
    ctx.fill(this.path2D)
    ctx.restore()
  }
}

if (!customElements.get('advent-calendar')) {
  customElements.define('advent-calendar', AdventCalendar)
}
